import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Route, Switch } from 'react-router-dom';
import LinearProgress from '@material-ui/core/LinearProgress';
import Paper from '@material-ui/core/Paper';

import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import NoResults from 'common/components/search-results/no-results';
import WideResults, {
  branchPlusRow,
  customerRow,
  offerRow,
  policyRow
} from 'common/components/search-results/wide-results';
import { isBranchEnv } from 'brand-config';
import { formatResultCount } from '../search-link';
import { ALL_RESULTS_ROUTE, visibleSearchTypes } from '../../search-types';
import useStyles from './condensed-results.styles';

// The All view previews the first rows of each type's first page, which runSearch has already loaded.
export const ALL_VIEW_PREVIEW_ROWS = 5;

/**
 * The condensed layout's results: one wide table per record type on the same routes, gates and per-type
 * pagination as the classic tables, plus an All view grouping every type.
 */
const CondensedResults = observer(function CondensedResults({ page, onPageChange }) {
  const classes = useStyles();
  const { search } = useStore();
  const session = useSession();
  const { canEdit, canQuote, viewOnly, isInternalAgent } = session;
  const { loading, error } = search;

  const toRows = {
    customers: (hits) => hits.map(customerRow),
    policies: (hits) => hits.map(policyRow),
    offers: (hits) => hits.map((offer) => offerRow(offer, { canOpen: canQuote || viewOnly })),
    branchPlus: (hits) => hits.map(branchPlusRow)
  };
  const rowsOf = (key, resultsKey) => toRows[key](search[resultsKey]?.hits ?? []);
  const totalOf = (resultsKey) => search[resultsKey]?.nbHits ?? 0;

  const typeResults = (type) => (
    <WideResults
      title={type.label}
      rows={rowsOf(type.key, type.resultsKey)}
      totalHits={totalOf(type.resultsKey)}
      loading={loading}
      page={page}
      onPageChange={onPageChange}
      error={error}
    />
  );
  const types = Object.fromEntries(visibleSearchTypes(session).map((type) => [type.key, type]));
  const allGroups = Object.values(types).filter((type) => totalOf(type.resultsKey) > 0);

  return (
    <Switch>
      <Route path={ALL_RESULTS_ROUTE}>
        {allGroups.map((type) => (
          <WideResults
            key={type.key}
            title={type.label}
            rows={rowsOf(type.key, type.resultsKey).slice(0, ALL_VIEW_PREVIEW_ROWS)}
            totalHits={totalOf(type.resultsKey)}
            loading={loading}
            seeAllTo={type.to}
            seeAllLabel={`See all ${formatResultCount(totalOf(type.resultsKey))}`}
            error={error}
          />
        ))}
        {allGroups.length === 0 && (
          <Paper className={classes.empty}>{loading ? <LinearProgress /> : <NoResults error={error} />}</Paper>
        )}
      </Route>
      {/* Same gate as the classic Offers route, which is wider than the Offers chip's. */}
      {(canEdit || viewOnly) && (
        <Route path="/search/offers">
          {typeResults(types.offers ?? { key: 'offers', label: 'Offers', resultsKey: 'offersResults' })}
        </Route>
      )}
      <Route path="/search/customers">{typeResults(types.customers)}</Route>
      <Route path="/search/policies">{typeResults(types.policies)}</Route>
      {isInternalAgent && isBranchEnv && <Route path="/search/branch-plus">{typeResults(types.branchPlus)}</Route>}
    </Switch>
  );
});

CondensedResults.propTypes = {
  page: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired
};

export default CondensedResults;
